import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  TrendingUp, TrendingDown, Minus, Headphones, Mic, BookOpen, PenTool,
  ArrowLeft, Target, Brain, BarChart3, Award, ChevronDown, ChevronUp,
  ArrowRight, Calendar, Loader2, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import AuthContext from "@/contexts/AuthContext";
import MultipleMeasuresDisclaimer from "@/components/MultipleMeasuresDisclaimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOD_CONFIG = {
  listening: { icon: Headphones, color: "#002FA7", bg: "bg-indigo-50", label: "Listening" },
  speaking: { icon: Mic, color: "#3b82f6", bg: "bg-blue-50", label: "Speaking" },
  reading: { icon: BookOpen, color: "#22c55e", bg: "bg-green-50", label: "Reading" },
  writing: { icon: PenTool, color: "#f59e0b", bg: "bg-amber-50", label: "Writing" },
};

const DIM_LABELS = {
  linguistic_complexity: { label: "Linguistic Complexity", short: "LC", desc: "Sentence variety & discourse level" },
  vocabulary_usage: { label: "Vocabulary Usage", short: "VU", desc: "Word choice precision & range" },
  language_control: { label: "Language Control", short: "LCtrl", desc: "Grammar, spelling & conventions" },
  organization: { label: "Organization", short: "Org", desc: "Text structure & coherence" },
};

const TRAJECTORY_STYLES = {
  growing: { icon: TrendingUp, color: "text-green-600", bg: "bg-green-50", border: "border-green-200", label: "Growing" },
  stable: { icon: Minus, color: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200", label: "Stable" },
  declining: { icon: TrendingDown, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Declining" },
};

const LEVEL_COLORS = ["", "#ef4444", "#f97316", "#eab308", "#3b82f6", "#6366f1", "#22c55e"];
const LEVEL_NAMES = { 1: "Entering", 2: "Emerging", 3: "Developing", 4: "Expanding", 5: "Bridging", 6: "Reaching" };

// SVG sparkline for dimensional scores over time
const DimensionSparkline = ({ points, color, height = 48, width = 200 }) => {
  if (!points || points.length < 2) return null;
  const max = 6, min = 0;
  const padding = 4;
  const chartW = width - padding * 2;
  const chartH = height - padding * 2;

  const coords = points.map((val, i) => ({
    x: padding + (i / (points.length - 1)) * chartW,
    y: padding + chartH - ((val - min) / (max - min)) * chartH,
  }));

  const pathD = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ");
  const areaD = `${pathD} L ${coords[coords.length - 1].x} ${height - padding} L ${coords[0].x} ${height - padding} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxWidth: width }}>
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {[1, 2, 3, 4, 5, 6].map(l => {
        const y = padding + chartH - ((l - min) / (max - min)) * chartH;
        return <line key={l} x1={padding} y1={y} x2={width - padding} y2={y} stroke="#e5e7eb" strokeWidth="0.5" />;
      })}
      {/* Area fill */}
      <path d={areaD} fill={`url(#grad-${color.replace('#', '')})`} />
      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots */}
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r="3" fill="white" stroke={color} strokeWidth="1.5" />
      ))}
      {/* Latest value label */}
      <text x={coords[coords.length - 1].x + 6} y={coords[coords.length - 1].y + 4} fill={color} fontSize="10" fontWeight="bold">
        {points[points.length - 1]}
      </text>
    </svg>
  );
};

// Dimension progress bar with first → latest
const DimensionBar = ({ dim, data, color }) => {
  const label = DIM_LABELS[dim]?.label || dim;
  const traj = TRAJECTORY_STYLES[data.trajectory] || TRAJECTORY_STYLES.stable;
  const TrajIcon = traj.icon;

  return (
    <div className="flex items-center gap-3 py-2" data-testid={`dim-bar-${dim}`}>
      <div className="w-40 text-sm font-medium text-gray-700 truncate">{label}</div>
      <div className="flex-1 flex items-center gap-2">
        <span className="text-xs text-gray-400 w-8 text-right">{data.first}</span>
        <div className="flex-1 bg-gray-100 h-2.5 relative">
          <div className="absolute left-0 top-0 h-full opacity-30" style={{ width: `${(data.first / 6) * 100}%`, backgroundColor: color }} />
          <div className="absolute left-0 top-0 h-full" style={{ width: `${(data.latest / 6) * 100}%`, backgroundColor: color }} />
        </div>
        <span className="text-xs font-bold w-8" style={{ color }}>{data.latest}</span>
      </div>
      <div className={`flex items-center gap-1 px-2 py-0.5 ${traj.bg} border ${traj.border} ${traj.color} text-xs font-semibold w-24 justify-center`}>
        <TrajIcon size={12} />
        {data.change > 0 ? "+" : ""}{data.change}
      </div>
    </div>
  );
};

const GrowthTrajectoryReport = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMod, setExpandedMod] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await axios.get(`${API}/reports/growth-trajectory/${studentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        toast.error("Failed to load growth trajectory");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [studentId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-[#002FA7]" size={32} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-center">
        <p className="text-gray-500">No data available for this student.</p>
        <Button onClick={() => navigate(-1)} variant="outline" className="mt-4">
          <ArrowLeft size={16} className="mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const { student, timelines, growth_summary, predictions, goals, sessions_with_rubric } = data;

  // Count modalities with dimensional data
  const modsWithData = Object.entries(growth_summary).filter(([_, g]) => g.has_data).length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="growth-trajectory-report">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button onClick={() => navigate(-1)} variant="outline" size="sm" data-testid="back-btn">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="report-title">
            Growth Trajectory Report
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {student.name} — {student.grade_level || "Grade N/A"} — {sessions_with_rubric} session{sessions_with_rubric !== 1 ? "s" : ""} with rubric data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/parent-report/${studentId}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors"
            data-testid="parent-report-link"
          >
            <FileText size={14} /> Parent Report
          </a>
          <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
            <Brain size={20} className="text-white" />
          </div>
        </div>
      </div>

      {/* No Data State */}
      {modsWithData === 0 && (
        <div className="border-2 border-dashed border-gray-300 p-10 text-center mb-8" data-testid="no-data-state">
          <Brain size={40} className="text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-700 mb-1">No Dimensional Data Yet</h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            This student hasn't taken any speaking or writing assessments with Automated rubric support yet.
            Dimensional trajectory tracking begins after the first rubric-scored session.
          </p>
        </div>
      )}

      {/* Predictions Card */}
      {Object.keys(predictions).length > 0 && (
        <div className="border-2 border-black p-6 mb-8" data-testid="predictions-card">
          <div className="flex items-center gap-2 mb-4">
            <Target size={18} className="text-[#002FA7]" />
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Projected MOSAIC Practice-Estimate Trend
            </h2>
            <span className="text-xs text-gray-400 ml-auto">Based on linear growth trend</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["listening", "speaking", "reading", "writing"].map(mod => {
              const pred = predictions[mod];
              if (!pred) return (
                <div key={mod} className="bg-gray-50 border border-gray-200 p-3 text-center">
                  <div className="text-xs text-gray-400 uppercase">{MOD_CONFIG[mod].label}</div>
                  <div className="text-sm text-gray-500 mt-1">No data</div>
                </div>
              );
              const Icon = MOD_CONFIG[mod].icon;
              const diff = pred.projected - pred.current;
              const goal = goals[mod];
              const onTrack = goal ? pred.projected >= goal : null;

              return (
                <div key={mod} className={`border-2 p-4 ${MOD_CONFIG[mod].bg} border-gray-200`} data-testid={`pred-${mod}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon size={14} style={{ color: MOD_CONFIG[mod].color }} />
                    <span className="text-xs font-semibold uppercase" style={{ color: MOD_CONFIG[mod].color }}>
                      {MOD_CONFIG[mod].label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl font-black" style={{ color: MOD_CONFIG[mod].color }}>{pred.current}</span>
                    <ArrowRight size={14} className="text-gray-400" />
                    <span className="text-2xl font-black" style={{ color: LEVEL_COLORS[Math.round(pred.projected)] || MOD_CONFIG[mod].color }}>
                      {pred.projected}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {diff > 0 ? "+" : ""}{diff.toFixed(1)} projected
                    {pred.confidence !== "high" && ` (${pred.confidence})`}
                  </div>
                  {goal && (
                    <div className={`text-xs mt-1 font-semibold ${onTrack ? "text-green-600" : "text-amber-600"}`}>
                      Goal: {goal} — {onTrack ? "On track" : "Behind"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-Modality Dimensional Breakdown */}
      {["listening", "speaking", "reading", "writing"].map(mod => {
        const summary = growth_summary[mod];
        const timeline = timelines[mod] || [];
        const cfg = MOD_CONFIG[mod];
        const Icon = cfg.icon;
        const isExpanded = expandedMod === mod;
        const hasDims = summary?.has_data && summary?.dimensions;

        if (!summary?.has_data) return null;

        const overallTraj = TRAJECTORY_STYLES[summary.overall_trajectory] || TRAJECTORY_STYLES.stable;
        const OverallTrajIcon = overallTraj.icon;

        return (
          <div key={mod} className="border-2 border-gray-200 mb-6" data-testid={`modality-card-${mod}`}>
            {/* Modality Header */}
            <button
              onClick={() => setExpandedMod(isExpanded ? null : mod)}
              className="w-full p-5 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              data-testid={`modality-toggle-${mod}`}
            >
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: cfg.color + '15', color: cfg.color }}>
                <Icon size={20} />
              </div>
              <div className="flex-1 text-left">
                <div className="font-bold text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{cfg.label}</div>
                <div className="text-xs text-gray-500">{summary.data_points} data point{summary.data_points !== 1 ? "s" : ""}</div>
              </div>
              {hasDims && (
                <div className={`flex items-center gap-1.5 px-3 py-1 ${overallTraj.bg} border ${overallTraj.border} ${overallTraj.color} text-sm font-semibold`}>
                  <OverallTrajIcon size={14} />
                  {summary.overall_trajectory === "growing" ? "+" : summary.overall_trajectory === "declining" ? "" : ""}{summary.avg_change} avg
                </div>
              )}
              {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
            </button>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t border-gray-200">
                {hasDims ? (
                  <>
                    {/* Dimension Bars */}
                    <div className="p-5 border-b border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Dimensional Growth (First → Latest)
                      </div>
                      {Object.entries(summary.dimensions).map(([dim, dimData]) => (
                        <DimensionBar key={dim} dim={dim} data={dimData} color={cfg.color} />
                      ))}
                    </div>

                    {/* Sparkline Charts */}
                    {timeline.length >= 2 && (
                      <div className="p-5 border-b border-gray-100">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                          Score Trends Over Time
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {["linguistic_complexity", "vocabulary_usage", "language_control", ...(mod === "writing" ? ["organization"] : [])].map(dim => {
                            const points = timeline.map(t => t[dim]).filter(v => v !== null && v !== undefined);
                            if (points.length < 2) return null;
                            return (
                              <div key={dim} className="bg-gray-50 border border-gray-200 p-3" data-testid={`sparkline-${mod}-${dim}`}>
                                <div className="text-xs font-semibold text-gray-600 mb-2">{DIM_LABELS[dim]?.label}</div>
                                <DimensionSparkline points={points} color={cfg.color} height={56} width={260} />
                                <div className="text-[10px] text-gray-400 mt-1">{DIM_LABELS[dim]?.desc}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Session History Table */}
                    {timeline.length > 0 && (
                      <div className="p-5">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                          Session History
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`history-table-${mod}`}>
                            <thead>
                              <tr className="border-b border-gray-200 text-left">
                                <th className="py-2 pr-3 text-xs text-gray-500 font-semibold">Date</th>
                                <th className="py-2 px-3 text-xs text-gray-500 font-semibold">LC</th>
                                <th className="py-2 px-3 text-xs text-gray-500 font-semibold">VU</th>
                                <th className="py-2 px-3 text-xs text-gray-500 font-semibold">LCtrl</th>
                                {mod === "writing" && <th className="py-2 px-3 text-xs text-gray-500 font-semibold">Org</th>}
                                <th className="py-2 px-3 text-xs text-gray-500 font-semibold">Overall</th>
                                <th className="py-2 pl-3 text-xs text-gray-500 font-semibold">Responses</th>
                              </tr>
                            </thead>
                            <tbody>
                              {timeline.map((t, i) => (
                                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-2 pr-3 text-xs text-gray-600 flex items-center gap-1">
                                    <Calendar size={10} className="text-gray-400" /> {t.date || "N/A"}
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className="font-bold text-sm" style={{ color: LEVEL_COLORS[t.linguistic_complexity] }}>{t.linguistic_complexity || "—"}</span>
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className="font-bold text-sm" style={{ color: LEVEL_COLORS[t.vocabulary_usage] }}>{t.vocabulary_usage || "—"}</span>
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className="font-bold text-sm" style={{ color: LEVEL_COLORS[t.language_control] }}>{t.language_control || "—"}</span>
                                  </td>
                                  {mod === "writing" && (
                                    <td className="py-2 px-3">
                                      <span className="font-bold text-sm" style={{ color: LEVEL_COLORS[t.organization] }}>{t.organization || "—"}</span>
                                    </td>
                                  )}
                                  <td className="py-2 px-3">
                                    <span className="font-bold" style={{ color: cfg.color }}>{t.overall || "—"}</span>
                                  </td>
                                  <td className="py-2 pl-3 text-xs text-gray-400">{t.response_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-5 text-center text-sm text-gray-500">
                    {timeline.length === 1 ? "Only 1 data point — need at least 2 sessions for growth tracking." : "No rubric-scored responses for this modality yet."}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Footer */}
      <div className="text-center text-xs text-gray-400 mt-8">
        <p>Generated {new Date(data.generated_at).toLocaleDateString()} — MOSAIC Growth Trajectory Report</p>
        <p className="mt-1">Automated rubric scores are suggestions — teacher scoring is authoritative. Trends shown are linear MOSAIC practice-estimate trends, not official WIDA ACCESS projections.</p>
      </div>

      {/* MOSAIC-NY1 · Pillar 4 · Multiple-Measures Disclaimer */}
      <MultipleMeasuresDisclaimer variant="full" className="mt-6" />
    </div>
  );
};

export default GrowthTrajectoryReport;
